import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useTheme,
  Tooltip,
} from '@mui/material'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { HelpOutlineRounded, SearchRounded } from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { fsColors } from '../../shared/constants'
import { setPtRuns } from '../../store/actions'
import FsDemoAccountModal from '../../components/common/molecules/DemoAccountModal'
import { getIsDemoAccount } from '../../utils'
import FsOFATextField from '../../components/fs-options-for-actions/fs-ofa-textfield'
import { ptAPI } from '../../shared/api/PTAPI'
import { PTInteractiveResult, PTKldB } from '../../shared/model'
import { useDebounce } from '../../shared/hooks'
import FsAlert, { FsAlertRef } from '../../components/common/atoms/Alert'
import FsPtResults from '../../components/fs-pt-interactive/fs-pt-results'
import PTBox from '../../components/fs-pt-interactive/fs-pt-box'
import PTModalEN from '../../components/fs-pt-interactive/fs-pt-modal-en'
import PTModalDE from '../../components/fs-pt-interactive/fs-pt-modal-de'
import { getIcon } from '../../components/common/Icons'
import FsFullPageLoader from '../../components/common/atoms/FullPageLoader'

const FsPtInteractiveView = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { ptRuns } = useAppSelector((state) => state.currentPt)
  const { currentLanguage, currentTheme } = useAppSelector((state) => state.currentUser)
  const dispatch = useAppDispatch()
  const [openDemoModal, setOpenDemoModal] = useState<boolean>(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const handleHelpOpen = () => setHelpOpen(true)
  const handleHelpClose = () => setHelpOpen(false)
  const [searchType, setSearchType] = useState<string>('1')
  const [searchQuery, setSearchQuery] = useState<string | null>(null)
  const [selectedKldb, setSelectedKldb] = useState<PTKldB | null>(null)
  const [salary, setSalary] = useState<number>(0)
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<PTKldB[]>([])
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<PTInteractiveResult[]>([])
  const [fetching, setFetching] = useState<boolean>(false)

  const alertRef = useRef<FsAlertRef>(null)

  useEffect(() => {
    dispatch(setPtRuns())
  }, [])

  const latestRun = ptRuns ? ptRuns[ptRuns.length - 1] : null

  const isDemoAccount = getIsDemoAccount()

  const debouncedsearch = useDebounce(async () => {
    if (latestRun) {
      try {
        const kldbs = await ptAPI.getPtInteractiveKldbs(
          latestRun.id,
          searchQuery ?? '',
          searchType,
          currentLanguage,
        )
        setOptions(kldbs)
      } catch {
        setLoading(false)
      } finally {
        setLoading(false)
      }
    }
  })

  const handleClose = () => {
    setOpen(false)
    setLoading(false)
    setSearchQuery(null)
    setOptions([])
  }

  useEffect(() => {
    if (latestRun) {
      if (searchQuery) {
        setOpen(true)
        setLoading(true)
        debouncedsearch()
      } else {
        handleClose()
      }
    }
  }, [searchQuery, latestRun])

  const fetchResults = async () => {
    if (isDemoAccount) {
      setOpenDemoModal(true)
      return
    }
    if (latestRun && selectedKldb && salary) {
      try {
        setFetching(true)
        const result = await ptAPI.getPtInteractiveResults(latestRun.id, selectedKldb.id, salary)
        if (!result || result.length === 0) {
          alertRef.current?.openAlert({ severity: 'error', message: t('something.went.wrong') })
        } else {
          setResults(result)
        }
      } catch {
        alertRef.current?.openAlert({ severity: 'error', message: t('something.went.wrong') })
      } finally {
        setFetching(false)
      }
    } else {
      alertRef.current?.openAlert({ severity: 'error', message: t('something.went.wrong') })
    }
  }

  return (
    <div>
      <FsAlert ref={alertRef} />
      <FsDemoAccountModal isOpen={openDemoModal} setIsOpen={setOpenDemoModal} app="pt" />
      <Stack mb={1} direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" ml={-0.5}>
          {getIcon('pt-assistance', { height: 30, width: 24 })}
          <Typography variant="h1" ml={1.5} mt={0.5}>
            {t('pt.assistance.title')}
          </Typography>
          <Box
            pl={1}
            pt={1}
            onClick={handleHelpOpen}
            sx={{ cursor: 'pointer', color: theme.palette.text.secondary }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>
        </Stack>
      </Stack>

      <Box sx={{ pb: 6, mx: 4 }}>
        {results?.length > 0 ? (
          <FsPtResults
            results={results}
            setResults={setResults}
            selectedKldb={selectedKldb}
            salary={salary}
            setFetching={setFetching}
          />
        ) : (
          <Box sx={{ overflowY: 'auto', height: 'calc(100vh - 148px)', pb: 4 }}>
            <Stack>
              <Typography variant="h2" className="my-3">
                {t('pt.assistance.select')}
              </Typography>
              <FormControl>
                <RadioGroup
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  name="controlled-radio-buttons-group"
                  value={searchType}
                  onChange={(event) => setSearchType(event.target.value)}
                  row
                >
                  <FormControlLabel
                    value="1"
                    control={<Radio />}
                    label={t('pt.assistance.select.occ')}
                    sx={{
                      '.MuiFormControlLabel-label': {
                        fontSize: '1rem',
                      },
                    }}
                  />
                  <FormControlLabel
                    value="2"
                    control={<Radio />}
                    label={t('pt.assistance.select.tat')}
                    sx={{
                      '.MuiFormControlLabel-label': {
                        fontSize: '1rem',
                      },
                    }}
                  />
                  <Tooltip
                    title={<Typography>{t('pt.assistance.tat.tooltip')}</Typography>}
                    arrow
                    placement="bottom"
                  >
                    <HelpOutlineRounded
                      fontSize="small"
                      sx={{ ml: -1, mt: 1.2, color: theme.palette.action.disabled }}
                    />
                  </Tooltip>
                </RadioGroup>
              </FormControl>
              <Autocomplete
                size="small"
                sx={{ mt: 1, maxWidth: 700 }}
                open={open}
                onClose={handleClose}
                isOptionEqualToValue={(option, value) =>
                  searchType === '1'
                    ? currentLanguage === 'de'
                      ? option.titleDe === value.titleDe
                      : option.titleEn === value.titleEn
                    : option.id === value.id
                }
                getOptionLabel={(option) =>
                  searchType === '1'
                    ? currentLanguage === 'de'
                      ? option.titleDe
                      : option.titleEn
                    : `${option.id}`
                }
                options={options}
                loading={loading}
                value={selectedKldb}
                onChange={(_, value) => setSelectedKldb(value)}
                noOptionsText={t('no.options')}
                loadingText={t('loading')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('search')}
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchRounded />,
                      endAdornment: loading ? (
                        <CircularProgress color="inherit" size={20} sx={{ marginRight: '-30px' }} />
                      ) : null,
                      style: {
                        height: '36px',
                        fontSize: '16px',
                        color:
                          currentTheme === 'dark' ? fsColors.fsGray.light : fsColors.fsGray.primary,
                      },
                    }}
                  />
                )}
              />
            </Stack>
            {selectedKldb && (
              <PTBox
                theme={theme}
                title={
                  <Typography className="font-semibold">
                    {t('pt.assistance.selected.occ')}
                  </Typography>
                }
                content={
                  <>
                    <Grid container>
                      <Grid size={{ xs: 6, md: 4 }}>
                        <Typography className="font-semibold">{`${t(
                          'pt.assistance.selected.occ.kldb',
                        )}:`}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 8 }}>
                        <Typography>{selectedKldb.id}</Typography>
                      </Grid>
                    </Grid>
                    <Grid container>
                      <Grid size={{ xs: 6, md: 4 }}>
                        <Typography className="font-semibold">{`${t(
                          'pt.assistance.selected.occ.main',
                        )}:`}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 8 }}>
                        <Typography>
                          {currentLanguage === 'de' ? selectedKldb.titleDe : selectedKldb.titleEn}
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                }
              />
            )}
            <Stack my={4}>
              <Typography variant="h2" className="my-3">
                {t('pt.assistance.salary.title')}
              </Typography>
              <Stack direction="row">
                <Typography mb={1}>{t('pt.assistance.salary.desc')}</Typography>
                <Tooltip
                  title={<Typography>{t('pt.assistance.salary.tooltip')}</Typography>}
                  arrow
                  placement="bottom"
                >
                  <HelpOutlineRounded
                    fontSize="small"
                    sx={{ ml: 0.5, color: theme.palette.action.disabled }}
                  />
                </Tooltip>
              </Stack>
              <Stack sx={{ maxWidth: 300, ml: -1 }}>
                <FsOFATextField
                  unit={`\u20ac`}
                  value={salary}
                  onChange={(cost: number) => {
                    setSalary(cost)
                  }}
                  noDebounce
                />
              </Stack>
            </Stack>
            <Button variant="contained" disabled={!salary || !selectedKldb} onClick={fetchResults}>
              {t('pt.assistance.show.results')}
            </Button>
          </Box>
        )}
      </Box>
      {currentLanguage === 'de' ? (
        <PTModalDE isOpen={helpOpen} onClose={handleHelpClose} />
      ) : (
        <PTModalEN isOpen={helpOpen} onClose={handleHelpClose} />
      )}
      {fetching && <FsFullPageLoader />}
    </div>
  )
}

export default FsPtInteractiveView
